import { Message, Chat } from 'src/store/models/channel/index.js'
import GlobalPrototypes from 'src/config/services/globalPrototypes.ts'

const defaultState = () => ({
  replyMessageId: {},
  pendingMessages: {},
  playingVoiceMessages: {},
  uploadProgress: {},
  newMessage: null,
  ariaLiveStatusMessages: [],
})

export const state = defaultState()

export const mutations = {
  RESET_STATE (state) {
    Object.assign(state, defaultState())
  },
  SET_REPLY_ID (state, { replyId, chatId }) {
    state.replyMessageId = {
      ...state.replyMessageId,
      [chatId]: replyId
    }
  },
  SET_PENDING_MESSAGES_ADD (state, { pendingMessage, chatId }) {
    state.pendingMessages = {
      ...state.pendingMessages,
      [chatId]: [
        ...state.pendingMessages[chatId] || [],
        pendingMessage
      ]
    }
  },
  SET_PENDING_MESSAGES_REMOVE (state, { pendingMessageId, chatId }) {
    state.pendingMessages = {
      ...state.pendingMessages,
      [chatId]: state.pendingMessages[chatId].filter(s => s.uuid !== pendingMessageId)
    }
  },
  SET_CURRENT_MESSAGE (state, { newMessage, chatId }) {
    state.newMessage = {
      ...state.newMessage,
      [chatId]: newMessage
    }
  },
  SET_MESSAGE_PENDING_STATE (state, { messageUuid, chatId, failed }) {
    const messageIndex = state.pendingMessages[chatId].findIndex(s => s.uuid === messageUuid)
    state.pendingMessages[chatId][messageIndex].sendFailed = failed
  },
  SET_FILE_UPLOAD_PROGRESS (state, { progress, id, abortController, file }) {
    state.uploadProgress = { ...state.uploadProgress, [id]: { progress, abortController, id, file } }
  },
  SET_PENDING_FILE_UPLOAD (state, { id }) {
    if (!state.uploadProgress) state.uploadProgress = {}
    const filter = Object.entries(state.uploadProgress)
      .filter(([_, value]) => value.id !== id)
    state.uploadProgress = Object.fromEntries(filter)
  },
  SET_PLAYING_VOICE_MESSAGES (state, { uuid, recorder }) {
    state.playingVoiceMessages = { ...state.playingVoiceMessages, [uuid]: recorder }
  },
  STOP_PLAYING_VOICE_MESSAGES (state, { uuid }) {
    const activeRecorder = state.playingVoiceMessages[uuid]
    if (!activeRecorder) return
    activeRecorder.pause()
    activeRecorder.stop()
    activeRecorder.setPlaybackRate(1)
  },
  TOGGLE_PLAYING_VOICE_MESSAGES (state, { uuid }) {
    const activeRecorder = state.playingVoiceMessages[uuid]
    if (!activeRecorder) return
    activeRecorder.playPause()
  },
  SET_PLAYBACK_RATE_VOICE_MESSAGES (state, { uuid, rate }) {
    const activeRecorder = state.playingVoiceMessages[uuid]
    if (!activeRecorder) return
    activeRecorder.setPlaybackRate(rate)
  },
  DESTROY_PLAYING_VOICE_MESSAGES (state) {
    Object.values(state.playingVoiceMessages).filter(el => el).map(el => el.destroy())
    state.playingVoiceMessages = {}
  },
  ADD_ARIA_LIVE_STATUS_MESSAGE (state, message) {
    if (!message) return
    state.ariaLiveStatusMessages.push(message)
  },
  RESET_ARIA_LIVE_STATUS_MESSAGE (state,) {
    state.ariaLiveStatusMessages = []
  }
}

export const getters = {
  GET_PLAYING_VOICE_MESSAGES (state) {
    return state.playingVoiceMessages
  },

  GET_ARIA_LIVE_STATUS_MESSAGES (state) {
    return state.ariaLiveStatusMessages
  }
}

const buildMessage = (content, replyId, chatId, file, channelId, surveyFormId, type) => {
  const reply = replyId
    ? Message.query()
      .where('uuid', replyId)
      .with('user')
      .with('file').first()
    : null
  return {
    content: content,
    content_rendered: content,
    created_at: new Date(),
    status: 'pending',
    uuid: GlobalPrototypes.uuid(),
    isMine: true,
    file: file,
    type: type,
    promise: null,
    unset_at: null,
    reply_uuid: state.replyMessageId[chatId] || '',
    reply: reply,
    chat_id: chatId,
    survey_form_id: surveyFormId
  }
}

const sendMessageObject = async (commit, message, chatId, channelId, cbFn) => {
  commit('SET_PENDING_MESSAGES_ADD', { pendingMessage: message, chatId: chatId })
  commit('SET_REPLY_ID', { replyId: '', chatId: chatId })

  try {
    await cbFn()
    commit('SET_PENDING_MESSAGES_REMOVE', { pendingMessageId: message.uuid, chatId: chatId })
  } catch (e) {
    // TODO: find better way to catch not allowed case
    if (e.message.includes('403')) {
      commit('SET_PENDING_MESSAGES_REMOVE', { pendingMessageId: message.uuid, chatId: chatId })
    } else {
      commit('SET_MESSAGE_PENDING_STATE', { messageUuid: message.uuid, chatId: chatId, failed: true })
    }
  }

  commit('SET_CURRENT_MESSAGE', { newMessage: '', chatId: chatId })
}

export const actions = {
  async sendMessage ({ commit }, { content, replyId, chatId, file, channelId, surveyFormId, abortController }) {
    const message = buildMessage(content, replyId, chatId, file, channelId, surveyFormId, file ? 'UPLOADING' : 'MESSAGE')
    return sendMessageObject(commit, message, chatId, channelId, async () => { await Chat.sendMessage(message, chatId, channelId, abortController) })
  },

  async sendKnock ({ commit }, { content, chatId, channelId }) {
    const message = buildMessage(content, null, chatId, null, channelId, null, null, 'KNOCK')
    return sendMessageObject(commit, message, chatId, channelId, async () => { await Chat.sendKnock(chatId, channelId, message.content) })
  },

  async sendVoice ({ commit }, { content, chatId, channelId, file, duration }) {
    const message = buildMessage(content, null, chatId, file, channelId, null, null, 'VOICE')
    return sendMessageObject(commit, message, chatId, channelId, async () => { await Chat.sendVoice(chatId, channelId, message, duration) })
  },

  async deletePendingMessage ({ commit }, { chatId, uuid }) {
    await commit('SET_PENDING_MESSAGES_REMOVE', { pendingMessageId: uuid, chatId: chatId })
  },

  async resendPendingMessage ({ commit, state }, { chatId, uuid, channelId, abortController }) {
    const message = state.pendingMessages[chatId].find(s => s.uuid === uuid)
    commit('SET_MESSAGE_PENDING_STATE', { messageUuid: message.uuid, chatId: chatId, failed: false })

    // TODO - refactor this part to avoid code duplication
    try {
      if (message?.type === 'VOICE') {
        await Chat.sendVoice(chatId, channelId, message, message.file?.duration_in_seconds)
      } else if (message?.type === 'KNOCK') {
        await Chat.sendKnock(chatId, channelId, message.content)
      } else {
        await Chat.sendMessage(message, chatId, Number(channelId), abortController)
      }

      commit('SET_PENDING_MESSAGES_REMOVE', { pendingMessageId: message.uuid, chatId: chatId })
    } catch (e) {
      // TODO: find better way to catch not allowed case
      if (e.message.includes('403')) {
        commit('SET_PENDING_MESSAGES_REMOVE', { pendingMessageId: message.uuid, chatId: chatId })
      } else {
        commit('SET_MESSAGE_PENDING_STATE', { messageUuid: message.uuid, chatId: chatId, failed: true })
      }
    }
  }
}
